import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import moment from 'moment-timezone';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';

import {
  useMutationShape, locationShape, historyShape, intlShape, growlShape,
} from '../../../components/prop-types';
import { withGrowl } from '../../../components/GrowlNotifications';
import Paginator from '../../../components/Paginator';
import { parse, sortUrl, toSortOrder } from '../../../utils/UrlUtil';
import RespondentImportListHeader from './RespondentImportListHeader';
import { withAccount } from '../../../components/AccountContext';

const messages = defineMessages({
  activate: { id: 'actions.activate', defaultMessage: 'Activate' },
  delete: { id: 'actions.delete', defaultMessage: 'Delete' },
  fileName: { id: 'attrs.fileName', defaultMessage: 'File Name' },
  created: { id: 'attrs.created', defaultMessage: 'Created' },
  updated: { id: 'attrs.updated', defaultMessage: 'Updated' },
  status: { id: 'attrs.import.status', defaultMessage: 'Status' },
});

const statusTemplate = (rowData) => {
  switch (rowData.status) {
    case 'VALID_IMPORT': {
      return (
        <FormattedMessage id="file_import.valid" defaultMessage="Valid">
          {text => <span className="filter filter--info is-active">{text}</span>}
        </FormattedMessage>
      );
    }
    case 'FAILED_IMPORT': {
      return (
        <FormattedMessage id="ile_import.failed" defaultMessage="Failed">
          {text => <span className="filter filter--error is-active">{text}</span>}
        </FormattedMessage>
      );
    }
    case 'ACTIVATED': {
      return (
        <FormattedMessage id="file_import.activated" defaultMessage="Activated">
          {text => <span className="filter filter--success is-active ">{text}</span>}
        </FormattedMessage>
      );
    }
    default: {
      return null;
    }
  }
};

const nameTemplate = (rowData, { currentAccount }) => {
  const detailUrl = `/accounts/${currentAccount}/respondents/imports/${rowData.id}`;
  return (
    <Link to={detailUrl} className="app-link">
      {rowData.fileName}
    </Link>
  );
};

const timeFieldTemplate = (rowData, { formatDate, field }) => {
  const updated = formatDate(moment(rowData[field]), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

class RespondentImportList extends Component {
  constructor(props) {
    super(props);
    this.onSortChange = this.onSortChange.bind(this);
    this.openContextMenu = this.openContextMenu.bind(this);
    this.actionsTemplate = this.actionsTemplate.bind(this);
    this.buildMenu = this.buildMenu.bind(this);
    this.onActivateImport = this.onActivateImport.bind(this);
    this.onDeleteImport = this.onDeleteImport.bind(this);
  }

  state = {
    menu: [],
  };

  onSortChange(event) {
    const { location, history } = this.props;
    sortUrl(event, location, history);
  }

  async onActivateImport(id) {
    const { activateImport, currentAccount, growl } = this.props;

    try {
      await activateImport.mutation({
        variables: {
          input: {
            accountId: currentAccount,
            importId: id,
            overwrite: false,
          },
        },
      });
    } catch (error) {
      growl.show({ severity: 'error', detail: error.message, sticky: true });
    }
  }

  async onDeleteImport(id) {
    const { deleteImport, currentAccount, growl } = this.props;

    try {
      await deleteImport.mutation({
        variables: {
          input: {
            accountId: currentAccount,
            importId: id,
          },
        },
      });
    } catch (error) {
      growl.show({ severity: 'error', detail: error.message, sticky: true });
    }
  }

  actionsTemplate(rowData) {
    return (
      <Button
        icon="pi pi-ellipsis-h"
        className="p-button-secondary no-border"
        onClick={e => this.openContextMenu(e, rowData)}
      />
    );
  }

  buildMenu(rowData) {
    const {
      intl: { formatMessage },
    } = this.props;

    const items = [];

    if (rowData.status === 'VALID_IMPORT') {
      items.push({
        label: formatMessage(messages.activate),
        // icon: 'fa fa-paper-plane',
        command: () => this.onActivateImport(rowData.id),
      });
    }

    items.push({
      label: formatMessage(messages.delete),
      icon: 'pi pi-times',
      command: () => this.onDeleteImport(rowData.id),
    });

    return items;
  }

  openContextMenu(event, rowData) {
    this.setState({
      menu: this.buildMenu(rowData),
    });
    this.menu.toggle(event);
  }

  render() {
    const {
      connection,
      intl: { formatMessage, formatDate },
      currentAccount,
      loading,
      location,
    } = this.props;
    const {
      edges, totalCount, indexFrom, indexTo, pageInfo,
    } = connection;
    // data list
    const listRows = edges.map(({ node }) => ({
      ...node,
    }));

    const params = parse(location.search);
    const { sort = {} } = params;
    const { sortBy, sortDir } = sort;

    // header
    const fileNameHeader = formatMessage(messages.fileName);
    const createdHeader = formatMessage(messages.created);
    const updatedHeader = formatMessage(messages.updated);
    const statusHeader = formatMessage(messages.status);

    // footer
    const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

    const { menu } = this.state;

    return (
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <Panel>
              <div className="p-col-12">
                <RespondentImportListHeader currentAccount={currentAccount} />
                <Menu
                  model={menu}
                  popup
                  ref={(el) => {
                    this.menu = el;
                    return el;
                  }}
                  appendTo={document.body}
                />
              </div>
              <div className="p-col-12">
                <DataTable
                  value={listRows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={this.onSortChange}
                  lazy
                  loading={loading}
                  autoLayout
                >
                  <Column
                    field="fileName"
                    header={fileNameHeader}
                    body={nameTemplate}
                    keyField="id"
                    currentAccount={currentAccount}
                    sortable
                  />
                  <Column
                    field="createdOn"
                    header={createdHeader}
                    sortable
                    body={timeFieldTemplate}
                    formatDate={formatDate}
                  />
                  <Column
                    field="updatedOn"
                    header={updatedHeader}
                    body={timeFieldTemplate}
                    formatDate={formatDate}
                    sortable
                  />
                  <Column field="status" header={statusHeader} body={statusTemplate} />
                  <Column body={this.actionsTemplate} style={{ width: '3.5em' }} />
                </DataTable>
              </div>
              <div className="p-col-12 align-center">{footer}</div>
            </Panel>
          </div>
        </div>
      </div>
    );
  }
}

RespondentImportList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  loading: PropTypes.bool.isRequired,
  location: locationShape.isRequired,
  history: historyShape.isRequired,
  activateImport: useMutationShape.isRequired,
  deleteImport: useMutationShape.isRequired,
  growl: growlShape.isRequired,
};

export default withAccount(withRouter(injectIntl(withGrowl(RespondentImportList))));
